/* ─────────────────────────────────────────────────────────────
   PlacesSelect — renderer for `dataSource: { type: 'places' }` fields.

   Backed by usePlacesOptions (TanStack Query, 24h staleTime).
   Handles cascading: parent field value drives the parentId query variable.
   Supports allowFreeText mode with debounced ES fuzzy suggestion fallback.

   States:
   - no-parent: disabled Combobox + "Pick a country first" hint
   - loading:   pulse skeleton
   - error:     inline error with retry button
   - empty:     "No subdivisions found" notice (with free-text fallback if enabled)
   - normal:    Combobox with filterable list
   - free-text: Combobox accepts input + ES suggestions shown inline
   - deprecated: read-only annotation badge when place.isActive === false

   Pre-codegen note (T01): uses PlaceOption / PlaceSearchHit from
   usePlacesOptions (local interfaces). T02 will swap to generated types.
   ───────────────────────────────────────────────────────────── */

import { useCallback, useMemo } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import type { FieldRendererProps, PlacesDataSource } from '../../config/types';
import {
  usePlacesOptions,
  usePlaceById,
  type PlaceSearchHit,
} from '../../query/usePlacesOptions';
import { Combobox } from '@/components/ui/Combobox';
import { Badge } from '@/components/ui/Badge';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';

/* ─────────────────────────────────────────────────────────────
   Main renderer
   ───────────────────────────────────────────────────────────── */

export function PlacesSelectRenderer({
  field,
  value,
  onChange,
  error,
  runtimeContext,
}: FieldRendererProps) {
  const ds = field.dataSource as PlacesDataSource;

  // FR-3: Dev-only naming assertion — UUID-emitting places fields must end in "_place_id".
  // Catches form-config errors early (e.g. `field: 'country'` instead of `field: 'country_place_id'`).
  // Production builds skip this check; Vite/Rollup dead-code-eliminates the branch when DEV=false.
  if (import.meta.env.DEV) {
    const emitsUUID = !ds.valueField || ds.valueField === 'id';
    if (emitsUUID && !field.field.endsWith('_place_id')) {
      throw new Error(
        `[PlacesSelect] Field "${field.field}" uses a places dataSource that emits UUIDs ` +
          `(valueField: "${ds.valueField ?? 'id'}") but its name does not end in "_place_id". ` +
          `Rename the field to "${field.field}_place_id" in the form config, ` +
          `or set valueField: "isoCode" / "canonicalName" if the mutation expects a denormalised string.`,
      );
    }
  }

  const level = ds.level;
  const allowFreeText = ds.allowFreeText ?? false;

  // ENT-50: resolve parent field value from the enriched runtimeContext.
  // FormBuilder (hasLiveExpressions path) spreads live form values into runtimeContext,
  // so runtimeContext[parentField] holds the current sibling field value.
  //
  // ds.parentValueField controls which path to use:
  //   'id' (default) → UUID cascade (entity-level, parentId)
  //   'isoCode'       → ISO code cascade (address region, parentIsoCodeValue)
  //
  // The ISO path triggers usePlaceByIsoCode to resolve isoCode → UUID before
  // issuing SubdivisionsByParent.
  const { parentId, parentIsoCodeValue } = useMemo<{
    parentId: string | null;
    parentIsoCodeValue: string | null;
  }>(() => {
    if (!ds.parentField) return { parentId: null, parentIsoCodeValue: null };
    const parentVal = runtimeContext[ds.parentField];
    if (typeof parentVal !== 'string' || !parentVal) {
      return { parentId: null, parentIsoCodeValue: null };
    }
    // Route based on explicit parentValueField config (not format guessing).
    if (ds.parentValueField === 'isoCode') {
      return { parentId: null, parentIsoCodeValue: parentVal };
    }
    // Default: parentValueField === 'id' or unset → UUID path
    return { parentId: parentVal, parentIsoCodeValue: null };
  }, [ds.parentField, ds.parentValueField, runtimeContext]);

  const {
    options,
    rawPlaces,
    isLoading,
    isError,
    retry,
    searchSuggestions,
    isSearching,
    setSearchQuery,
  } = usePlacesOptions({
    level,
    parentId: ds.parentField ? parentId : undefined,
    parentIsoCode: ds.parentIsoCode,
    parentIsoCodeValue: ds.parentField ? parentIsoCodeValue : undefined,
    allowFreeText,
    // ENT-50: pass valueField so usePlacesOptions projects the correct Combobox value.
    // 'id' (default) = UUID, 'isoCode' = ISO-2 string, 'canonicalName' = display name.
    valueField: ds.valueField,
  });

  // Fetch the current value as a Place for deprecated annotation
  const currentValue = value as string | undefined;
  const { place: currentPlace } = usePlaceById(
    currentValue && rawPlaces.length === 0 ? currentValue : null,
  );

  const handleChange = useCallback(
    (v: string) => {
      onChange(v || null);
    },
    [onChange],
  );

  /* ── No-parent state: parent field present but no value yet ── */
  if (ds.parentField && !parentId && !parentIsoCodeValue) {
    // Override the config placeholder in this disabled state — the
    // BE-served `field.placeholder` is typically tuned for the
    // "ready-to-select" state, which would be misleading when the
    // control is disabled. The fixed copy explains *why* it's disabled
    // and what the user needs to do next.
    return (
      <DisabledPlaceholder
        id={field.id}
        placeholder="Select a country first to see available jurisdictions"
        error={error}
      />
    );
  }

  /* ── Loading state ── */
  if (isLoading) {
    return <PlacesSelectSkeleton id={field.id} />;
  }

  /* ── Error state ── */
  if (isError) {
    return <PlacesSelectError id={field.id} onRetry={retry} error={error} />;
  }

  /* ── Deprecated annotation (read-only display with current value) ── */
  if (currentValue && currentPlace && currentPlace.isActive === false) {
    return (
      <DeprecatedPlaceDisplay
        id={field.id}
        place={currentPlace}
        onClear={() => handleChange('')}
        error={error}
      />
    );
  }

  /* ── Empty state (no options, no parent restriction issue) ── */
  if (options.length === 0 && !allowFreeText) {
    return (
      <PlacesSelectEmpty id={field.id} label="No subdivisions found" error={error} />
    );
  }

  /* ── ES suggestion options to merge into Combobox ── */
  const suggestionOptions = allowFreeText
    ? searchSuggestions.map((hit: PlaceSearchHit) => ({
        value: hit.place.id,
        label: hit.place.canonicalName,
        description: hit.matchedOn ? `matched on: ${hit.matchedOn}` : undefined,
        group: 'Suggestions',
        skipFilter: true as const,
      }))
    : [];

  // Merge primary options + ES suggestions (dedup by value)
  const seenIds = new Set(options.map((o) => o.value));
  const mergedOptions = [
    ...options,
    ...suggestionOptions.filter((s) => !seenIds.has(s.value)),
  ];

  // ENT-51: Do NOT render the validation error message here — FieldWrapper owns
  // the error message rendering. Rendering it in both places caused the
  // "Country is required" message to appear twice (regression from ENT-50).
  // We still use `error` to drive the danger border styling on the Combobox.
  return (
    <div>
      <Combobox
        id={field.id}
        options={mergedOptions}
        value={currentValue ?? ''}
        onValueChange={handleChange}
        onSearch={allowFreeText ? setSearchQuery : undefined}
        placeholder={field.placeholder ?? (allowFreeText ? 'Type to search...' : 'Select...')}
        searchPlaceholder="Search..."
        emptyMessage={
          isSearching
            ? 'Searching...'
            : allowFreeText
              ? 'No match — keep typing to search all countries'
              : 'No results found'
        }
        disabled={field.isDisabled}
        clearable
        grouped={suggestionOptions.length > 0}
        className={`bg-surface-elevated ${error ? 'border-danger' : ''}`}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────────────────────────── */

function DisabledPlaceholder({
  id,
  placeholder,
  error,
}: {
  id: string;
  placeholder: string;
  error?: string;
}) {
  // ENT-51: error message rendering owned by FieldWrapper; here we only use
  // `error` to switch the border colour for visual cue.
  return (
    <div>
      <div
        id={id}
        className={`flex h-10 cursor-not-allowed items-center rounded-lg border border-border
          bg-surface-muted px-3 opacity-60
          ${error ? 'border-danger' : 'border-border'}`}
        aria-disabled="true"
      >
        <Text variant="body-sm" color="subtle">
          {placeholder}
        </Text>
      </div>
    </div>
  );
}

function PlacesSelectSkeleton({ id }: { id: string }) {
  return (
    <div
      id={id}
      className="h-10 w-full animate-pulse rounded-lg bg-surface-muted"
      role="status"
      aria-label="Loading places..."
    />
  );
}

function PlacesSelectError({
  id,
  onRetry,
  error,
}: {
  id: string;
  onRetry: () => void;
  error?: string;
}) {
  return (
    <div className="space-y-1">
      <div
        id={id}
        className="flex h-10 items-center gap-2 rounded-lg border border-danger/40
          bg-danger-subtle px-3"
        role="alert"
      >
        <AlertCircle size={14} className="text-danger shrink-0" />
        <Text variant="body-sm" color="danger">
          {error ?? 'Failed to load options'}
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="ml-auto h-6 gap-1 text-danger hover:text-danger"
          aria-label="Retry loading places"
        >
          <RefreshCw size={12} />
          Retry
        </Button>
      </div>
    </div>
  );
}

function PlacesSelectEmpty({
  id,
  label,
  error,
}: {
  id: string;
  label: string;
  error?: string;
}) {
  // ENT-51: error message rendering owned by FieldWrapper; we keep the prop
  // signature for the (future) styling cue but no longer render the message.
  void error;
  return (
    <div>
      <div
        id={id}
        className="flex h-10 items-center rounded-lg border border-border px-3"
        role="status"
        aria-label={label}
      >
        <Text variant="body-sm" color="muted">
          {label}
        </Text>
      </div>
    </div>
  );
}

function DeprecatedPlaceDisplay({
  id,
  place,
  onClear,
  error,
}: {
  id: string;
  place: { id: string; canonicalName: string; isoCode?: string | null };
  onClear: () => void;
  error?: string;
}) {
  // ENT-51: error message rendering owned by FieldWrapper.
  void error;
  return (
    <div className="space-y-1">
      <div
        id={id}
        className="flex h-10 items-center gap-2 rounded-lg border border-warning/40
          bg-warning-subtle px-3"
      >
        <Text variant="body-sm" color="default">
          {place.canonicalName}
          {place.isoCode && (
            <span className="ml-1 text-text-muted">({place.isoCode})</span>
          )}
        </Text>
        <Badge variant="warning" className="ml-auto shrink-0">
          deprecated
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="ml-1 h-6 text-text-muted hover:text-text"
          aria-label="Clear deprecated place"
        >
          ×
        </Button>
      </div>
    </div>
  );
}
